/* eslint-disable no-undef */
/* eslint-disable camelcase */
import axios from 'axios';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useCallback, useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { setTest } from '../../../../actions/config.actions';
import { setCallbackAuth, setGlobalAlert, setOffer, setupOauthSettings, showSplashScreen, updateOffer } from '../../../../actions/offer.actions';
import { INFLUENCERS_ENDPOINTS } from '../../../../config/api';
import Button from '../../../../shared/components/Button';
import FlashSuccess from '../../../../shared/components/FlashSuccess/FlashSuccess';
import ApplySidePane from '../ApplySidePane/ApplySidePane';
import DeclineSidePane from '../DeclineSidePane/DeclineSidePane';
import './PromptOffer.scss';
import SurveySidePane from '../SurveySidePane/SurveySidePane';

/**
 *
 * @param {{
 *  canJoin: boolean
 *  maxReached: string
 *  paused: boolean
 * }} params
 * @returns
 */
function PromptOffer({
  canJoin,
  maxReached,
  paused,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const isLogged = useSelector(store => get(store, 'auth.user'));
  const isInfluencerWithOfferAccessToken = useSelector(store => get(store, 'auth.isInfluencerWithOfferAccessToken'));
  const offer = useSelector(store => get(store, 'offer.offer'));
  const simulatedUserId = localStorage.getItem('simulatedUser');
  /** @type {import('../../../../reducers/offer.reducer').OfferStore} */
  const offerId = useSelector(store => get(store, 'offer.offerId'));
  const userSignedUp = useSelector(store => get(store, 'offer.userSignedUp'));
  const { token: accessToken } = queryString.parse(window.location.search);
  const loggedOutInfluencerWithSurveyAccess = isInfluencerWithOfferAccessToken &&
    accessToken &&
    accessToken == offer.access_token &&
    offer.otype == 'survey' &&
    offer.logged_out_survey;
  const dispatch = useDispatch();

  // remove admin check when tipalti is ready
  const tipaltiPaymentSetupNeeded = offer.tipalti_payment_processing && !user.is_payable_by_tipalti && user.admin;

  const [state, setState] = useState({
    openApplySidePane: false,
    renderApplySidePane: false,
    openDeclineSidePane: false,
    renderDeclineSidePane: false,
    openSurveySidePane: false,
    renderSurveySidePane: false,
    showSuccessApply: false,
  });

  useEffect(() => {
    // Should open automatically the side pane
    if (!userSignedUp && get(offer, 'user_offer.status') === 'applying' && (!tipaltiPaymentSetupNeeded || simulatedUserId)) {
      toggleApplyForOffer(true);
    }
  }, [userSignedUp]);

  useEffect(() => {
    const queryParams = queryString.parse(location.search);
    let {
      youtube_oauth_callback,
      facebook_oauth_callback,
      test_flow = ''
    } = queryParams;

    const setupFacebookOAuth = {
      required: offer.facebook_oauth_required,
      optional: offer.facebook_oauth_optional,
    };
    const setupYoutubeOAuth = {
      // process: offer.youtube_oauth_publishing,
      required: offer.youtube_oauth_publishing,
    };

    // Possible flows for testing: fb_required | fb_optional | fb_success | fb_error | yt | yt_success | yt_error
    if (test_flow) {
      dispatch(setTest(test_flow.split(',')));

      if (test_flow.includes('yt_error')) {
        setupYoutubeOAuth.process = true;
        setupYoutubeOAuth.response = 'error';
        toggleApplyForOffer(true);
      } else if (test_flow.includes('yt_success')) {
        setupYoutubeOAuth.process = true;
        setupYoutubeOAuth.response = 'success';
        toggleApplyForOffer(true);
      }
      if (test_flow.includes('yt')) setupYoutubeOAuth.required = true;

      if (test_flow.includes('fb_error')) {
        setupFacebookOAuth.process = true;
        setupFacebookOAuth.response = 'error';
        toggleApplyForOffer(true);
      } else if (test_flow.includes('fb_success')) {
        setupFacebookOAuth.process = true;
        setupFacebookOAuth.response = 'success';
        dispatch(showSplashScreen(
          true,
          `🤩📱✨\n${t('influencers.pages.campaign.apply_side_pane.facebook_connect.ig_connected_successfully')}`,
        ));
        toggleApplyForOffer(true);
      }
      if (test_flow.includes('fb_required')) setupFacebookOAuth.required = true;
      if (test_flow.includes('fb_optional')) setupFacebookOAuth.optional = true;
    } else {
      dispatch(setTest([]));
      dispatch(setupOauthSettings({
        facebook: {},
        youtube: {}
      }));
    }

    if (["true", "false"].includes(youtube_oauth_callback)) {
      toggleApplyForOffer(true);
      setupYoutubeOAuth.process = true;
      setupYoutubeOAuth.response = youtube_oauth_callback === 'true'
        ? 'success'
        : 'error';
    }

    if (["true", "false"].includes(facebook_oauth_callback)) {
      setupFacebookOAuth.process = true;
      if (facebook_oauth_callback === 'true') {
        dispatch(showSplashScreen(
          true,
          `🤩📱✨\n${t('influencers.pages.campaign.apply_side_pane.facebook_connect.ig_connected_successfully')}`,
        ));
        setupFacebookOAuth.response = 'success';
      } else {
        setupFacebookOAuth.response = 'error';
      }
      toggleApplyForOffer(true);
    }


    dispatch(setupOauthSettings({
      facebook: setupFacebookOAuth,
      youtube: setupYoutubeOAuth
    }));

  }, []);

  const toggleDeclinePane = useCallback((open, render = true) => {
    document.body.style.overflow = open ? 'hidden' : 'unset';
    setState({
      ...state,
      renderDeclineSidePane: render,
      openDeclineSidePane: open,
    });
  }, [state]);

  const onSaveInfo = () => {
    let user_offer = { skip_validation: true }; // we don't need to validate the survey

    axios.post(INFLUENCERS_ENDPOINTS.OFFERS.USER_OFFERS.POST(offerId), {
      user_offer,
    }).then(({ data }) => {
      const offerTmp = {
        ...offer,
        user_offer: { ...data.user_offer }
      };
      dispatch(setOffer(offerTmp));
      onAfterCloseApply(!offer.is_survey);
    });
  };

  /**
   * @param {boolean} open If side pane is open or not
   * @param {boolean} render If side pane should render or not, this is for when the side pane is created but not opened
   */
  const toggleApplyForOffer = useCallback((open, extra = {}) => {
    if (offer.is_survey) {
      onSaveInfo();
      return;
    }

    document.body.style.overflow = open ? 'hidden' : 'unset';
    setState({
      ...state,
      renderApplySidePane: true,
      openApplySidePane: open,
      ...extra,
    });

    if (get(extra, 'savedApplication')) {
      dispatch(setGlobalAlert({
        content: <React.Fragment>
          <i className="icon fal fa-check-circle"></i>
          {t('influencers.pages.campaign.apply_side_pane.application_saved')}
        </React.Fragment>,
        variant: 'success',
        shouldUpdateOffer: true
      }));
    }

  }, [state]);

  const toggleSurveyPane = useCallback((open, extra = {}) => {
    document.body.style.overflow = open ? 'hidden' : 'unset';
    setState({
      ...state,
      renderSurveySidePane: true,
      openSurveySidePane: open,
      ...extra,
    });
  }, [state]);

  /**
   * @param {boolean} applied If the user has applied or not the offer
   */
  const onAfterCloseApply = useCallback((applied) => {
    setState({
      ...state,
      openApplySidePane: false,
      renderApplySidePane: false,
      showSuccessApply: applied,
    });

    if (offer.is_survey) {
      dispatch(updateOffer(offerId));
    }

    // Remove query params for OAuth
    const queryParams = new URLSearchParams(location.search);

    if (queryParams.has('youtube_oauth_callback')) {
      queryParams.delete('youtube_oauth_callback');
      history.replace({ search: queryParams.toString() });
      dispatch(setCallbackAuth(null));
    }

    if (queryParams.has('facebook_oauth_callback')) {
      queryParams.delete('facebook_oauth_callback');
      history.replace({ search: queryParams.toString() });
      dispatch(setCallbackAuth(null));
    }
  }, [state]);

  const PromptContent = () => {
    let content;
    
    if (loggedOutInfluencerWithSurveyAccess) {
      content = t('influencers.pages.campaign.im_interested')
    } else if (!isLogged) {
      content = t('influencers.pages.campaign.sign_in_to_join');
    } else if (isLogged && !canJoin && !maxReached) {
      content = t('influencers.pages.campaign.not_available');
    } else if (isLogged && canJoin && !maxReached && !paused && tipaltiPaymentSetupNeeded && !simulatedUserId) {
      content = <Trans i18nKey='influencers.pages.campaign.tipalti_payment_setup_needed' components={[<Link to="/profile/payments" />]}> </Trans>;
    } else if (isLogged && canJoin && !maxReached && !paused) {
      content = offer.is_survey ? t('influencers.pages.campaign.join_this_survey') : t('influencers.pages.campaign.join_this_campaign');
    } else if (isLogged && maxReached) {
      content = maxReached;
    } else if (isLogged && canJoin && paused) {
      content = t('influencers.pages.campaign.maximum_reached');
    }

    return <h6 className="card-header-title">{content}</h6>;
  };

  return <div className="prompt">
    <Card>
      <div className="content">
        <Card.Header className="p-3">
          <PromptContent />
        </Card.Header>
        { loggedOutInfluencerWithSurveyAccess &&
          <Button variant="tertiary" onClick={() => toggleSurveyPane(true)}>
            <span><b>{t('influencers.pages.campaign.start_survey')}</b></span>
          </Button>
        }
        {!loggedOutInfluencerWithSurveyAccess && isLogged && canJoin && !maxReached && !paused && (!tipaltiPaymentSetupNeeded || simulatedUserId) &&
          <Card.Footer className="pt-2 pb-3">
            <Button variant="tertiary" onClick={() => {
              toggleDeclinePane(true, true);
            }}>
              <i className="fal fa-times icon-left"></i>
              {t('influencers.common.decline')}
            </Button>
            <Button variant="tertiary" onClick={() => toggleApplyForOffer(true)}>
              <i className="fal fa-check icon-left"></i>
              {offer.is_survey ? t('influencers.common.start_survey') : t('influencers.common.apply')}
            </Button>
          </Card.Footer>
        }
        {!isLogged && !loggedOutInfluencerWithSurveyAccess &&
          <Card.Footer className="pb-3">
            <React.Fragment>
              <Link to="/sign-up/step-1">
                <Button variant="tertiary">
                  <i className="fal fa-edit icon-left"></i>
                  {t('influencers.common.new_account')}
                </Button>
              </Link>
              <Link to={{
                pathname: "/signin",
                state: { from: location.pathname }
              }}>
                <Button variant="tertiary">
                  <i className="fal fa-user icon-left"></i>
                  {t('influencers.common.sign_in')}
                </Button>
              </Link>
            </React.Fragment> 
          </Card.Footer>
        }
      </div>
    </Card >
    {
      state.renderDeclineSidePane && <DeclineSidePane
        open={state.openDeclineSidePane}
        onRequestClose={toggleDeclinePane}
        onAfterClose={() => toggleDeclinePane(false, false)}
      />
    }
    {
      state.renderApplySidePane && <ApplySidePane
        open={state.openApplySidePane}
        onRequestClose={(extra) => toggleApplyForOffer(false, extra)}
        onAfterClose={(applied) => onAfterCloseApply(applied)}
      />
    }
    {
      state.renderSurveySidePane && offer.survey && <SurveySidePane
        isPaneOpen={state.openSurveySidePane}
        onRequestClose={() => toggleSurveyPane(false)}
        typeformID={offer.survey.code}
      />
    }
    {
      state.showSuccessApply && <FlashSuccess timeout={3000}
        isOpen={state.showSuccessApply}
        onClose={() => {
          setState({
            ...state,
            showSuccessApply: false
          });
          dispatch(updateOffer(offerId));
        }}>
        <h3 className='text-center'>
          🚀💡🕺🏻
          {t('influencers.pages.campaign.apply_side_pane.application_submitted')}
        </h3>
      </FlashSuccess>
    }
  </div >;
}

PromptOffer.propTypes = {
  canJoin: PropTypes.bool,
  maxReached: PropTypes.string,
  paused: PropTypes.bool
};

export default React.memo(PromptOffer);
